// Extract the current edition's text; never paraphrase Scripture for the feed.
export function extractSocialVerses(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const seen = new Set();
  return Array.from(doc.querySelectorAll('em, td.vn')).flatMap(marker => {
    const refs = [...marker.textContent.matchAll(/(\d+):(\d+)/g)];
    if (!refs.length) return [];
    const [, chapter, verse] = refs[0];
    const id = `${chapter}-${verse}`;
    if (seen.has(id)) return [];
    const source = marker.matches('td') ? marker.closest('tr')?.querySelector('td.v')
      : marker.closest('.bible-reading-verse, .gal-verse, .hebrew-verse') || marker.nextElementSibling;
    if (!source) return [];
    const copy = source.cloneNode(true);
    copy.querySelectorAll('em, button, rt, rp, script, style').forEach(node => node.remove());
    const text = copy.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return [];
    seen.add(id);
    return [{ id, chapter: Number(chapter), verse: Number(verse),
      reference: refs.map(match => `${match[1]}:${match[2]}`).join(' / '), text }];
  });
}

export function shuffleSocialVerses(verses, previousId, random = Math.random) {
  const next = [...verses];
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  if (next.length > 1 && next[0].id === previousId) {
    [next[0], next[1]] = [next[1], next[0]];
  }
  return next;
}

// Editorial QT notes are written ahead of time, not generated or attributed to
// a biblical speaker. Matching keywords select a reading prompt, not a doctrine.
export const SOCIAL_TOPICS = [
  { id: 'love', label: '愛', pattern: /愛|慈しみ|\blov(?:e|es|ed|ing)\b/i, note: '「愛」ということばを、今日は急がずに読んでみる。だれに向けられた愛なのか、前後のことばにも耳を澄ませたい。' },
  { id: 'hope', label: '希望', pattern: /希望|望み|\bhope\b/i, note: '希望が語られる、その場面に立ち止まる。何を待ち、何を信じているのか。ひとつ前の節から、もう一度。' },
  { id: 'peace', label: '平安', pattern: /平安|平和|安ら|\bpeace\b/i, note: '「平安」は、どんな場面で語られているのだろう。ことばの響きと、その周りにある物語をいっしょに読みたい。' },
  { id: 'prayer', label: '祈り', pattern: /祈|\bpray(?:er|s|ed|ing)?\b/i, note: '祈る人は、何を抱えているのだろう。その願いにすぐ答えを出さず、まずはことばを受けとめてみる。' },
  { id: 'light', label: '光', pattern: /光|\blight\b/i, note: 'この箇所の「光」は、何を照らしているのだろう。ひとつのことばを入り口に、物語をたどってみたい。' },
  { id: 'fear', label: '恐れ', pattern: /恐れ|恐れる|おそれ|\bfear\b|afraid/i, note: '恐れが書かれていることにも、立ち止まってみる。だれが、何を恐れているのか。前後を読むと、見え方が変わるかもしれない。' },
  { id: 'wisdom', label: '知恵', pattern: /知恵|智慧|\bwisdom\b|\bwise\b/i, note: '知ることと、生きること。そのあいだをつなぐ知恵として、このことばを日常に持ち帰ってみたい。' },
  { id: 'joy', label: '喜び', pattern: /喜び|喜ぶ|よろこ|\bjoy\b|rejoic/i, note: 'ここにある喜びは、何から生まれたのだろう。よく知っているつもりのことばを、初めて読むように。' },
  { id: 'life', label: 'いのち', pattern: /いのち|命|\blife\b/i, note: '「いのち」ということばの前で、少しだけ読む速さを落とす。この場面で語られていることを、丁寧にたどりたい。' },
  { id: 'forgive', label: '赦し', pattern: /赦|ゆるし|forgiv/i, note: '赦しが語られるとき、そこにはどんな関係があるのだろう。引用した一節だけで終えず、続くことばも読んでみる。' },
];
export const SOCIAL_INTEREST_KEY = 'bible-social-interests-v1';
export function verseTopics(text) { return SOCIAL_TOPICS.filter(topic => topic.pattern.test(text)); }
export function readSocialInterests() {
  try {
    const stored = JSON.parse(localStorage.getItem(SOCIAL_INTEREST_KEY)) || {};
    return Object.fromEntries(SOCIAL_TOPICS.map(({ id }) => [id, Number.isFinite(stored[id]) ? Math.max(0, Math.min(30, stored[id])) : 0]));
  } catch { return {}; }
}
export function rememberSocialRead(text) {
  const next = readSocialInterests();
  for (const topic of verseTopics(text)) next[topic.id] = Math.min(30, (next[topic.id] || 0) + 1);
  try { localStorage.setItem(SOCIAL_INTEREST_KEY, JSON.stringify(next)); } catch { /* Reading works without storage. */ }
}
export function recommendSocialVerses(verses, interests, previousId, random = Math.random) {
  // Weighted random selection keeps room for discoveries beyond past interests.
  const ranked = verses.map(item => {
    const relevance = verseTopics(item.text).reduce((sum, topic) => sum + (interests[topic.id] || 0), 0);
    const weight = 1 + Math.min(4, relevance * 0.3);
    return { item, score: -Math.log(Math.max(Number.EPSILON, random())) / weight };
  }).sort((a, b) => a.score - b.score).map(({ item }) => item);
  if (ranked.length > 1 && ranked[0].id === previousId) [ranked[0], ranked[1]] = [ranked[1], ranked[0]];
  return ranked;
}
export function makeSocialCards(ordered, verses) {
  const positions = new Map(verses.map((item, index) => [item.id, index]));
  return ordered.map((item, index) => {
    const topic = verseTopics(item.text)[0];
    const following = verses.slice(positions.get(item.id), positions.get(item.id) + 3).filter(next => next.chapter === item.chapter);
    if (topic && index % 3 === 1) return { item, kind: 'quote', topic };
    if (index % 3 === 2 && following.length > 1) return { item, kind: 'thread', verses: following };
    return { item, kind: 'verse' };
  });
}
